/**
 * Analytics Service for tracking user engagement and interactions
 * Provides methods to track various user actions across the application
 */

/**
 * Track a custom event
 * @param {string} eventName - Name of the event
 * @param {object} eventParams - Additional parameters for the event
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, {
      ...eventParams,
      timestamp: new Date()?.toISOString(),
    });
  }
};

/**
 * Track navigation between pages
 * @param {string} fromPage - Source page
 * @param {string} toPage - Destination page
 */
export const trackNavigation = (fromPage, toPage) => {
  trackEvent('navigation', {
    from_page: fromPage,
    to_page: toPage,
    navigation_type: 'internal',
  });
};

/**
 * Track feature usage
 * @param {string} featureName - Name of the feature used
 * @param {object} additionalData - Additional context about feature usage
 */
export const trackFeatureUsage = (featureName, additionalData = {}) => {
  trackEvent('feature_usage', {
    feature_name: featureName,
    page: window.location?.pathname,
    ...additionalData,
  });
};

/**
 * Track AI chat interactions
 * @param {string} action - Type of interaction (open, close, message_sent, etc.)
 * @param {object} metadata - Additional metadata about the interaction
 */
export const trackAIChatInteraction = (action, metadata = {}) => {
  trackEvent('ai_chat_interaction', {
    action,
    page: window.location?.pathname,
    ...metadata,
  });
};

/**
 * Track dashboard interactions
 * @param {string} component - Component name (metric_card, chart, etc.)
 * @param {string} action - Action performed (view, click, expand, etc.)
 * @param {object} data - Additional data about the interaction
 */
export const trackDashboardInteraction = (component, action, data = {}) => {
  trackEvent('dashboard_interaction', {
    component,
    action,
    page: window.location?.pathname,
    ...data,
  });
};

/**
 * Track user session start
 * @param {object} userInfo - User information
 */
export const trackSessionStart = (userInfo = {}) => {
  trackEvent('session_start', {
    ...userInfo,
    session_id: generateSessionId(),
  });
};

/**
 * Track user login
 * @param {string} method - Login method used
 */
export const trackLogin = (method = 'email') => {
  trackEvent('login', {
    method,
  });
};

/**
 * Track metric card views
 * @param {string} metricName - Name of the metric viewed
 * @param {string} metricValue - Value of the metric
 */
export const trackMetricView = (metricName, metricValue) => {
  trackEvent('metric_view', {
    metric_name: metricName,
    metric_value: metricValue,
    page: window.location?.pathname,
  });
};

/**
 * Track chart interactions
 * @param {string} chartType - Type of chart (line, bar, etc.)
 * @param {string} action - Action performed (view, hover, click)
 */
export const trackChartInteraction = (chartType, action) => {
  trackEvent('chart_interaction', {
    chart_type: chartType,
    action,
    page: window.location?.pathname,
  });
};

/**
 * Track drill-down actions
 * @param {string} fromMetric - Source metric
 * @param {string} toDetail - Detail view accessed
 */
export const trackDrillDown = (fromMetric, toDetail) => {
  trackEvent('drill_down', {
    from_metric: fromMetric,
    to_detail: toDetail,
    page: window.location?.pathname,
  });
};

/**
 * Generate a unique session ID
 * @returns {string} Session ID
 */
const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
};

export default {
  trackEvent,
  trackNavigation,
  trackFeatureUsage,
  trackAIChatInteraction,
  trackDashboardInteraction,
  trackSessionStart,
  trackLogin,
  trackMetricView,
  trackChartInteraction,
  trackDrillDown,
};